
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Repeat, ArrowRightLeft, Sparkles, ShieldCheck, CheckCircle, Cpu, ArrowUpDown, Info, Zap } from 'lucide-react';
import { useWallet } from '../../../hooks/useWallet';
import { Token } from '../../../types';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { TokenSelector } from './TokenSelector';
import { FeeCard } from './FeeCard';
import { AiAnalyticsPanel } from './AiAnalyticsPanel';
import { TokenDetails } from './TokenDetails';

interface SwapBridgeContainerProps {
    initialMode: 'swap' | 'bridge';
}

export const SwapBridgeContainer: React.FC<SwapBridgeContainerProps> = ({ initialMode }) => {
    const { balances, isConnected } = useWallet();
    const [mode, setMode] = useState<'swap' | 'bridge'>(initialMode);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    // Asset Selection State
    const [fromToken, setFromToken] = useState<Token | null>(balances.find(b => b.symbol === 'SOL') || balances[0]);
    const [toToken, setToToken] = useState<Token | null>(balances.find(b => b.symbol === 'USDC') || balances[1]);
    const [amount, setAmount] = useState('');

    // Logic: Ensure cross-chain bridge validity
    useEffect(() => {
        if (mode === 'bridge' && fromToken && toToken) {
            if (fromToken.chain === toToken.chain) {
                const otherChain = fromToken.chain === 'SOL' ? 'SUI' : 'SOL';
                const crossToken = balances.find(b => b.chain === otherChain) || null;
                setToToken(crossToken);
            }
        }
    }, [mode, fromToken, balances]);

    const handleAction = async () => {
        if (!fromToken || !toToken || !amount) return;
        setIsProcessing(true);
        // Atomic simulation
        await new Promise(res => setTimeout(res, 1800));
        setIsProcessing(false);
        setShowSuccess(true);
        setAmount('');
        setTimeout(() => setShowSuccess(false), 4000);
    };

    const switchTokens = () => {
        const temp = fromToken;
        setFromToken(toToken);
        setToToken(temp);
    };

    const isInputValid = useMemo(() => {
        const num = parseFloat(amount);
        return !isNaN(num) && num > 0 && fromToken && num <= fromToken.balance;
    }, [amount, fromToken]);

    const exchangeRate = (fromToken && toToken) ? fromToken.priceUsd / toToken.priceUsd : 0;
    const outputValue = parseFloat(amount || '0') * exchangeRate;

    return (
        <div className="max-w-[1400px] mx-auto space-y-10 animate-fade-in pb-24 px-4 sm:px-0">
            {/* Minimalist Header */}
            <div className="flex flex-col md:flex-row justify-between items-center md:items-end gap-6 border-b border-stone-100 dark:border-white/5 pb-8">
                <div>
                    <h2 className="font-serif text-3xl text-[#0A0B0F] dark:text-white font-bold tracking-tight uppercase">
                        {mode} Assets
                    </h2>
                    <p className="text-stone-500 text-[10px] font-black uppercase tracking-[0.25em] mt-1 opacity-60">Ghost-Relay Institutional Rails</p>
                </div>
                <div className="flex p-1 bg-stone-100 dark:bg-white/5 rounded-sm border border-stone-200 dark:border-white/10">
                    <button 
                        onClick={() => setMode('swap')}
                        className={`flex items-center gap-2 px-6 py-2 rounded-sm text-[10px] font-black uppercase tracking-widest transition-all ${mode === 'swap' ? 'bg-[#0A0B0F] dark:bg-white text-white dark:text-black shadow-lg' : 'text-stone-400 hover:text-stone-900'}`}
                    >
                        <Repeat size={14} /> Swap
                    </button>
                    <button 
                        onClick={() => setMode('bridge')}
                        className={`flex items-center gap-2 px-6 py-2 rounded-sm text-[10px] font-black uppercase tracking-widest transition-all ${mode === 'bridge' ? 'bg-[#0A0B0F] dark:bg-white text-white dark:text-black shadow-lg' : 'text-stone-400 hover:text-stone-900'}`}
                    >
                        <ArrowRightLeft size={14} /> Bridge
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                {/* Left Side: Interaction Hub */}
                <div className="lg:col-span-7 space-y-4">
                    <Card noPadding className="border-stone-200 dark:border-white/5 rounded-sm bg-white dark:bg-[#15171C] shadow-2xl relative overflow-hidden">
                        {/* Transaction Panels */}
                        <div className="p-1 space-y-1 bg-stone-50 dark:bg-black/20">
                            {/* Input Card */}
                            <div className="p-8 bg-white dark:bg-[#1A1D23] rounded-sm transition-all border border-transparent hover:border-nobel-gold/20">
                                <div className="flex justify-between items-center mb-6">
                                    <span className="text-[10px] font-black text-stone-400 uppercase tracking-[0.3em]">Selling</span>
                                    {fromToken && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-[9px] font-mono font-bold text-stone-500 uppercase">Available: {fromToken.balance.toLocaleString()}</span>
                                            <button 
                                                onClick={() => setAmount(fromToken.balance.toString())}
                                                className="text-[9px] font-black text-nobel-gold hover:text-white hover:bg-nobel-gold px-2 py-0.5 rounded-sm border border-nobel-gold/30 transition-all uppercase tracking-tighter"
                                            >
                                                MAX
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-6">
                                    <input 
                                        type="number" 
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        className="flex-1 bg-transparent text-4xl sm:text-5xl font-mono font-bold outline-none text-[#0A0B0F] dark:text-white placeholder-stone-200 dark:placeholder-stone-800"
                                        placeholder="0.00"
                                    />
                                    <TokenSelector 
                                        label=""
                                        selected={fromToken} 
                                        onSelect={setFromToken} 
                                        balances={balances}
                                        chainFilter={mode === 'bridge' ? undefined : fromToken?.chain}
                                    />
                                </div>
                                <div className="mt-2 text-[10px] font-mono font-bold text-stone-400 uppercase tracking-tight">
                                    ≈ ${(parseFloat(amount || '0') * (fromToken?.priceUsd || 0)).toLocaleString(undefined, {minimumFractionDigits: 2})} USD
                                </div>
                            </div>

                            {/* Middle Action */}
                            <div className="flex justify-center -my-8 relative z-20">
                                <button 
                                    onClick={switchTokens}
                                    className="p-4 bg-white dark:bg-[#15171C] border border-stone-200 dark:border-white/10 rounded-sm text-stone-400 hover:text-nobel-gold shadow-2xl transition-all hover:scale-110 active:scale-95 group"
                                >
                                    <ArrowUpDown size={20} className="group-hover:rotate-180 transition-transform duration-500" />
                                </button>
                            </div>

                            {/* Output Card */}
                            <div className="p-8 bg-white dark:bg-[#1A1D23] rounded-sm transition-all border border-transparent hover:border-nobel-gold/20">
                                <div className="flex justify-between items-center mb-6">
                                    <span className="text-[10px] font-black text-stone-400 uppercase tracking-[0.3em]">Buying</span>
                                    <div className="flex items-center gap-1.5 text-[9px] font-bold text-emerald-500 uppercase tracking-widest bg-emerald-500/5 px-2 py-0.5 rounded-sm border border-emerald-500/10">
                                        <Zap size={10} fill="currentColor"/> Best Rate
                                    </div>
                                </div>
                                <div className="flex items-center gap-6">
                                    <div className="flex-1 text-4xl sm:text-5xl font-mono font-bold text-[#0A0B0F] dark:text-white opacity-90 truncate">
                                        {outputValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                                    </div>
                                    <TokenSelector 
                                        label=""
                                        selected={toToken} 
                                        onSelect={setToToken} 
                                        balances={balances}
                                        chainFilter={mode === 'bridge' ? (fromToken?.chain === 'SOL' ? 'SUI' : 'SOL') : fromToken?.chain}
                                    />
                                </div>
                                <div className="mt-2 text-[10px] font-mono font-bold text-stone-400 uppercase tracking-tight">
                                    Rate: 1 {fromToken?.symbol} = {exchangeRate.toFixed(6)} {toToken?.symbol}
                                </div>
                            </div>
                        </div>

                        {/* Execution Layer */}
                        <div className="p-8 space-y-6">
                            <FeeCard fromToken={fromToken} toToken={toToken} amount={parseFloat(amount) || 0} mode={mode} />
                            
                            <Button 
                                onClick={handleAction}
                                isLoading={isProcessing}
                                disabled={!isConnected || !isInputValid}
                                className="w-full h-18 rounded-sm text-base font-black uppercase tracking-[0.3em] shadow-2xl bg-[#0A0B0F] dark:bg-white text-white dark:text-black"
                            >
                                {mode === 'swap' ? 'Commit Atomic Swap' : 'Execute Bridge Sequence'}
                            </Button>
                        </div>
                    </Card>

                    <div className="flex items-center justify-center gap-6 py-4 opacity-40 grayscale">
                        <ShieldCheck size={18}/>
                        <span className="text-[9px] font-black uppercase tracking-[0.4em]">Audit Verified • Non-Custodial • 256-Bit Encrypted</span>
                    </div>
                </div>

                {/* Right Side: Telemetry & Intelligence */}
                <div className="lg:col-span-5 space-y-6">
                    <TokenDetails fromToken={fromToken} toToken={toToken} />
                    <AiAnalyticsPanel fromToken={fromToken} toToken={toToken} amount={parseFloat(amount) || 0} />
                    
                    <Card className="bg-[#0A0B0F] text-white p-8 rounded-sm border border-white/5 relative overflow-hidden group shadow-xl">
                        <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity rotate-12"><Sparkles size={160} /></div>
                        <div className="relative z-10">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="p-3 bg-white/5 rounded-sm text-nobel-gold border border-white/10">
                                    <Cpu size={24} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-xs uppercase tracking-[0.3em] leading-none">Runtime Performance</h3>
                                    <div className="text-[9px] text-stone-500 uppercase font-black tracking-widest mt-2">Node Cluster: SOL-042</div>
                                </div>
                            </div>
                            <p className="text-xs text-stone-400 leading-relaxed font-medium mb-8">
                                Ghost-Relay executes cross-chain intents via atomic burn-and-mint hooks. This ensures zero liquidity fragmentation and sub-second settlement on {mode === 'swap' ? 'native DEX rails' : 'LayerZero OFT infrastructure'}.
                            </p>
                            <div className="grid grid-cols-2 gap-8 border-t border-white/5 pt-8">
                                <div>
                                    <div className="text-[9px] text-stone-600 font-black uppercase tracking-widest mb-1.5">Consensus Delay</div>
                                    <div className="text-xl font-mono font-bold text-emerald-500">14MS</div>
                                </div>
                                <div>
                                    <div className="text-[9px] text-stone-600 font-black uppercase tracking-widest mb-1.5">Network Load</div>
                                    <div className="text-xl font-mono font-bold text-white">NOMINAL</div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            <AnimatePresence>
                {showSuccess && (
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
                        className="fixed bottom-10 right-10 z-50 bg-[#0A0B0F] border border-nobel-gold/50 p-6 rounded-sm shadow-2xl flex items-center gap-5 text-white max-w-sm"
                    >
                        <div className="w-12 h-12 rounded-sm bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/20 shrink-0">
                            <CheckCircle size={28} strokeWidth={3} />
                        </div>
                        <div>
                            <div className="font-serif font-bold text-lg leading-tight">Interaction Verified</div>
                            <div className="text-[10px] text-stone-500 font-mono mt-1.5 truncate">SIG: 0x7611862f472f10b7410eb705e7e...</div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
